import { css, type StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { XIconNew } from '../../../web/components/svgs/icons/XIcon';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { countActiveInboxFilters, type InboxFilterOption, type InboxFilters } from './inboxFilters';
import { FilterGlyph, SearchGlyph } from './inboxIcons';
import { inboxStyles as s } from './styles/inboxStyles';
import { TicketFilterPanel } from './TicketFilterPanel';

interface TicketSearchBarProps {
	value: string;
	onChange: (value: string) => void;
	filters: InboxFilters;
	onFiltersChange: (next: InboxFilters) => void;
	categoryOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
	assigneeOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
	/** False when the active result source imposes relevance order. */
	allowSort: boolean;
	/** Reset every filter to its default. */
	onClearAll: () => void;
	/** Focus the search input on mount — set when the bar expands from the list-header icon. */
	autoFocus?: boolean;
	/** Container overrides for hosts that embed the bar inline (the table view's views row). */
	styles?: StyleDeclarationValue[];
	/** Popover placement overrides for hosts where spanning the container doesn't fit. */
	panelStyles?: StyleDeclarationValue[];
}

/**
 * Ticket search: a single free-text box (matching customer email, subject, and message content
 * server-side) plus a "Search criteria" popover toggle. The toggle carries a badge with the count
 * of active narrowing filters; the popover ({@link TicketFilterPanel}) owns the structured
 * Status/Priority/Category/Assignee/Sort controls. The inline × clears only the query text, so an
 * agent can drop the search without losing their filters.
 *
 * The popover is a hand-rolled panel (click-away overlay + absolutely positioned card) rather than
 * the v2 Popover on purpose: its rows use the v2 Select, whose option list portals to the body, and
 * that would register as an outside interaction and dismiss a Radix Popover on every selection.
 */
export const TicketSearchBar: React.FC<TicketSearchBarProps> = ({
	value,
	onChange,
	filters,
	onFiltersChange,
	categoryOptions,
	assigneeOptions,
	allowSort,
	onClearAll,
	autoFocus,
	styles = [],
	panelStyles,
}) => {
	const [open, setOpen] = React.useState(false);
	const triggerRef = React.useRef<HTMLButtonElement>(null);
	const panelRef = React.useRef<HTMLDivElement>(null);
	const wasOpenRef = React.useRef(false);
	const activeCount = countActiveInboxFilters(filters);

	React.useEffect(() => {
		if (!open) {
			if (wasOpenRef.current) triggerRef.current?.focus();
			wasOpenRef.current = false;
			return;
		}

		wasOpenRef.current = true;
		const firstControl = panelRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea');
		firstControl?.focus();
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				setOpen(false);
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [open]);

	return (
		<div className={css(s.searchBar, ...styles)}>
			<div className={css(s.searchRow)}>
				<div className={css(s.searchInputWrap)}>
					<SearchGlyph className={css(s.searchIcon)} size={15} />
					<Input
						styles={[s.searchInput, ...(value ? [s.searchInputClearable] : [])]}
						value={value}
						placeholder='Search by email or keyword'
						aria-label='Search tickets'
						autoFocus={autoFocus}
						onChange={e => onChange(e.target.value)}
					/>
					{value && (
						<Button
							type='button'
							variant='ghost'
							styles={[s.searchClearBtn]}
							aria-label='Clear search'
							onClick={() => onChange('')}
						>
							<XIconNew width={13} height={13} />
						</Button>
					)}
				</div>
				<Button
					ref={triggerRef}
					type='button'
					variant='ghost'
					styles={[s.filterToggle, open && s.filterToggleActive]}
					aria-label='Search criteria'
					aria-haspopup='dialog'
					aria-expanded={open}
					onClick={() => setOpen(prev => !prev)}
				>
					<FilterGlyph size={17} />
					{activeCount > 0 && <span className={css(s.filterBadge)}>{activeCount}</span>}
				</Button>
			</div>

			{open && (
				<>
					<div className={css(s.filterOverlay)} onClick={() => setOpen(false)} />
					<TicketFilterPanel
						panelRef={panelRef}
						styles={panelStyles}
						filters={filters}
						onFiltersChange={onFiltersChange}
						categoryOptions={categoryOptions}
						assigneeOptions={assigneeOptions}
						allowSort={allowSort}
						onClearAll={onClearAll}
						onClose={() => setOpen(false)}
					/>
				</>
			)}
		</div>
	);
};
